import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import api from '../../services/api'
import { User } from '../../types'

export interface UserState {
  users: User[]
  selectedUser: User | null
  loading: boolean
  actionLoading: boolean
  error: string | null
  stats: {
    total: number
    active: number
    inactive: number
    admins: number
    developers: number
  } | null
}

const initialState: UserState = {
  users: [],
  selectedUser: null,
  loading: false,
  actionLoading: false,
  error: null,
  stats: null,
}

// Async thunks for admin user management
export const fetchAllUsers = createAsyncThunk(
  'users/fetchAll',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/admin/users')
      return response.data.data
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch users')
    }
  }
)

export const createUser = createAsyncThunk(
  'users/create',
  async (userData: Partial<User>, { rejectWithValue }) => {
    try {
      const response = await api.post('/admin/users', userData)
      return response.data.data.user
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create user')
    }
  }
)

export const updateUser = createAsyncThunk(
  'users/update',
  async ({ userId, userData }: { userId: string; userData: Partial<User> }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/admin/users/${userId}`, userData)
      return response.data.data.user
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update user')
    }
  }
)

export const deleteUser = createAsyncThunk(
  'users/delete',
  async (userId: string, { rejectWithValue }) => {
    try {
      await api.delete(`/admin/users/${userId}`)
      return userId
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete user')
    }
  }
)

export const updateUserStatus = createAsyncThunk(
  'users/updateStatus',
  async ({ userId, isActive }: { userId: string; isActive: boolean }, { rejectWithValue }) => {
    try {
      await api.put(`/admin/users/${userId}/status`, { isActive })
      return { userId, isActive }
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update user status')
    }
  }
)

export const fetchUserStats = createAsyncThunk(
  'users/fetchStats',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/admin/users/stats')
      return response.data.data
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch user statistics')
    }
  }
)

export const bulkUpdateUsers = createAsyncThunk(
  'users/bulkUpdate',
  async ({ action, userIds, data }: { action: string; userIds: string[]; data?: any }, { rejectWithValue }) => {
    try {
      const response = await api.post('/admin/users/bulk', { action, userIds, data })
      return response.data.data
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to perform bulk operation')
    }
  }
)

export const resetUserPassword = createAsyncThunk(
  'users/resetPassword',
  async ({ userId, password }: { userId: string; password?: string }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/admin/users/${userId}/reset-password`, { password })
      return response.data.message
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to reset password')
    }
  }
)

export const bulkCreateUsers = createAsyncThunk(
  'users/bulkCreate',
  async (users: Array<any>, { rejectWithValue }) => {
    try {
      const response = await api.post('/admin/users/bulk-create', { users })
      return response.data.data
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to bulk create users')
    }
  }
)

const userSlice = createSlice({
  name: 'users',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null
    },
    setSelectedUser: (state, action: PayloadAction<User | null>) => {
      state.selectedUser = action.payload
    },
    clearUsers: (state) => {
      state.users = []
      state.selectedUser = null
      state.stats = null
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all users
      .addCase(fetchAllUsers.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchAllUsers.fulfilled, (state, action) => {
        state.loading = false
        state.users = action.payload.users
        // Calculate basic stats
        state.stats = {
          total: action.payload.users.length,
          active: action.payload.users.filter((u: User) => u.isActive).length,
          inactive: action.payload.users.filter((u: User) => !u.isActive).length,
          admins: action.payload.users.filter((u: User) => u.role === 'admin').length,
          developers: action.payload.users.filter((u: User) => u.role === 'developer').length,
        }
      })
      .addCase(fetchAllUsers.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })

      // Create user
      .addCase(createUser.pending, (state) => {
        state.actionLoading = true
        state.error = null
      })
      .addCase(createUser.fulfilled, (state, action) => {
        state.actionLoading = false
        state.users.unshift(action.payload)
      })
      .addCase(createUser.rejected, (state, action) => {
        state.actionLoading = false
        state.error = action.payload as string
      })

      // Update user
      .addCase(updateUser.pending, (state) => {
        state.actionLoading = true
        state.error = null
      })
      .addCase(updateUser.fulfilled, (state, action) => {
        state.actionLoading = false
        const index = state.users.findIndex(user => user._id === action.payload._id)
        if (index !== -1) {
          state.users[index] = action.payload
        }
        if (state.selectedUser?._id === action.payload._id) {
          state.selectedUser = action.payload
        }
      })
      .addCase(updateUser.rejected, (state, action) => {
        state.actionLoading = false
        state.error = action.payload as string
      })

      // Delete user
      .addCase(deleteUser.pending, (state) => {
        state.actionLoading = true
        state.error = null
      })
      .addCase(deleteUser.fulfilled, (state, action) => {
        state.actionLoading = false
        state.users = state.users.filter(user => user._id !== action.payload)
        if (state.selectedUser?._id === action.payload) {
          state.selectedUser = null
        }
      })
      .addCase(deleteUser.rejected, (state, action) => {
        state.actionLoading = false
        state.error = action.payload as string
      })

      // Update user status
      .addCase(updateUserStatus.pending, (state) => {
        state.actionLoading = true
        state.error = null
      })
      .addCase(updateUserStatus.fulfilled, (state, action) => {
        state.actionLoading = false
        const { userId, isActive } = action.payload
        const user = state.users.find(u => u._id === userId)
        if (user) {
          user.isActive = isActive
        }
      })
      .addCase(updateUserStatus.rejected, (state, action) => {
        state.actionLoading = false
        state.error = action.payload as string
      })

      // Fetch user stats
      .addCase(fetchUserStats.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchUserStats.fulfilled, (state, action) => {
        state.loading = false
        state.stats = action.payload.totals
      })
      .addCase(fetchUserStats.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })

      // Bulk update users
      .addCase(bulkUpdateUsers.pending, (state) => {
        state.actionLoading = true
        state.error = null
      })
      .addCase(bulkUpdateUsers.fulfilled, (state) => {
        state.actionLoading = false
        // Refresh users list after bulk operation
      })
      .addCase(bulkUpdateUsers.rejected, (state, action) => {
        state.actionLoading = false
        state.error = action.payload as string
      })

      // Reset user password
      .addCase(resetUserPassword.pending, (state) => {
        state.actionLoading = true
        state.error = null
      })
      .addCase(resetUserPassword.fulfilled, (state) => {
        state.actionLoading = false
      })
      .addCase(resetUserPassword.rejected, (state, action) => {
        state.actionLoading = false
        state.error = action.payload as string
      })

      // Bulk create users
      .addCase(bulkCreateUsers.pending, (state) => {
        state.actionLoading = true
        state.error = null
      })
      .addCase(bulkCreateUsers.fulfilled, (state, action) => {
        state.actionLoading = false
        // Add newly created users to the list
        if (action.payload.created && action.payload.created.length > 0) {
          state.users = [...state.users, ...action.payload.created]
        }
      })
      .addCase(bulkCreateUsers.rejected, (state, action) => {
        state.actionLoading = false
        state.error = action.payload as string
      })
  },
})

export const { clearError, setSelectedUser, clearUsers } = userSlice.actions
export default userSlice.reducer
